// src/components/inspections/InspectionsView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/authClient';
import {
  ClipboardList, CheckCircle, AlertCircle, Clock, Calendar,
  Plus, Search, Download, FileText, FileSpreadsheet,
  ChevronDown, ChevronRight, Eye, Edit2, Trash2, X, User,
  Loader, RefreshCw, Activity
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import InspectionChecklist from './InspectionChecklist';
import InspectionExportModal from '../modal/InspectionExportModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Toast Notification Component ─────────────────────────────────────────────
const ToastNotification = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const colors = {
    success: 'bg-green-500/20 border-green-500 text-green-400',
    error: 'bg-red-500/20 border-red-500 text-red-400',
    warning: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    info: 'bg-blue-500/20 border-blue-500 text-blue-400'
  };
  
  return (
    <div className="fixed bottom-4 right-4 z-50 animate-toast-slide">
      <div className={`${colors[type]} border rounded-lg p-4 max-w-md shadow-xl flex items-start gap-3`}>
        {type === 'error' && <AlertCircle size={20} className="flex-shrink-0" />}
        {type === 'success' && <CheckCircle size={20} className="flex-shrink-0" />}
        {type === 'warning' && <AlertCircle size={20} className="flex-shrink-0" />}
        <div className="flex-1 text-sm whitespace-pre-wrap">{message}</div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const formatErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
    if (Array.isArray(data)) {
      return data.map(e => e.msg || e.message || JSON.stringify(e)).join('\n• ');
    }
  }
  if (error.message) return error.message;
  return defaultMessage;
};

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Modal d'assignation ──────────────────────────────────────────────────────
const AssignModal = ({ inspection, onAssign, onClose, staff, loading }) => {
  const [selectedStaffId, setSelectedStaffId] = useState(inspection?.assigned_to || '');
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    if (!selectedStaffId) {
      alert('Please select a staff member');
      return;
    }
    setSaving(true);
    try {
      await onAssign(inspection.id, selectedStaffId);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Assign Inspection</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Assign to Staff Member</label>
            <select
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">— Select staff —</option>
              {staff.map(s => (
                <option key={s.id} value={s.id}>
                  {s.first_name} {s.last_name} ({s.role})
                </option>
              ))}
            </select>
          </div>
          {inspection?.assigned_to_name && (
            <p className="text-gray-400 text-xs">Currently assigned to: {inspection.assigned_to_name}</p>
          )}
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={saving || !selectedStaffId}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <User size={14} />}
            Assign
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal d'édition ──────────────────────────────────────────────────────────
const EditModal = ({ inspection, onSave, onClose, templates, zones }) => {
  const [formData, setFormData] = useState({
    scheduled_date: inspection?.scheduled_date || '',
    scheduled_time: inspection?.scheduled_time || '09:00',
    template_id: inspection?.template_id || '',
    zone_id: inspection?.zone_id || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!formData.scheduled_date) {
      alert('Scheduled date is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(inspection.id, formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h3 className="text-white font-semibold">Edit Inspection</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Inspection #</label>
            <input
              type="text"
              value={inspection?.inspection_number || ''}
              disabled
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-gray-400 text-sm"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Template</label>
            <select
              value={formData.template_id}
              onChange={(e) => setFormData({...formData, template_id: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">Select template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.template_code} - {t.template_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Zone</label>
            <select
              value={formData.zone_id}
              onChange={(e) => setFormData({...formData, zone_id: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">Select zone...</option>
              {zones.map(z => (
                <option key={z.id} value={z.id}>{z.zone_code} - {z.zone_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Scheduled Date</label>
            <input
              type="date"
              value={formData.scheduled_date}
              onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">Scheduled Time</label>
            <input
              type="time"
              value={formData.scheduled_time}
              onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
              step="60"
            />
          </div>
        </div>
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm flex items-center gap-2"
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const InspectionsView = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [expandedId, setExpandedId] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [toast, setToast] = useState(null);
  
  // États pour les modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  
  const [templates, setTemplates] = useState([]);
  const [zones, setZones] = useState([]);
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    template_id: '',
    zone_id: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
    scheduled_time: '09:00',
    assigned_to: ''
  });

  const [stats, setStats] = useState({
    scheduled: 0,
    completed: 0,
    overdue: 0,
    passRate: 0
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await api.get('/company/settings');
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) {
          setLogoUrl(`${API_BASE}${response.data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  // Fetch inspections
  const fetchInspections = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        range: dateRange,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };
      const response = await api.get('/inspections/', { params });
      const data = response.data || [];
      setInspections(data);
      
      const scheduled = data.filter(i => i.status === 'scheduled').length;
      const completed = data.filter(i => i.status === 'completed').length;
      const overdue = data.filter(i => i.status === 'overdue').length;
      const passed = data.filter(i => i.overall_result === 'pass').length;
      const passRate = completed > 0 ? (passed / completed) * 100 : 0;
      
      setStats({ scheduled, completed, overdue, passRate: passRate.toFixed(1) });
    } catch (error) {
      console.error('Error fetching inspections:', error);
      showToast(formatErrorMessage(error, 'Error fetching inspections'), 'error');
      setInspections([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [dateRange, statusFilter]);

  // Charger les données pour les formulaires
  const loadFormData = useCallback(async () => {
    try {
      const [templatesRes, zonesRes, staffRes] = await Promise.all([
        api.get('/inspections/templates'),
        api.get('/airport/zones/'),
        api.get('/staff/')
      ]);
      setTemplates(templatesRes.data || []);
      setZones(zonesRes.data || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  }, []);

  // Créer une inspection
  const handleCreateInspection = async () => {
    if (!createForm.template_id || !createForm.scheduled_date) {
      showToast('Template and scheduled date are required', 'warning');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/inspections/', {
        template_id: createForm.template_id,
        zone_id: createForm.zone_id || null,
        scheduled_date: createForm.scheduled_date,
        scheduled_time: createForm.scheduled_time,
        assigned_to: createForm.assigned_to || null
      });
      showToast('Inspection created successfully', 'success');
      await fetchInspections();
      setShowCreateModal(false);
      setCreateForm({
        template_id: '',
        zone_id: '',
        scheduled_date: new Date().toISOString().slice(0, 10),
        scheduled_time: '09:00',
        assigned_to: ''
      });
    } catch (error) {
      console.error('Error creating inspection:', error);
      showToast(formatErrorMessage(error, 'Error creating inspection'), 'error');
    } finally {
      setCreating(false);
    }
  };

  // Assigner une inspection
  const handleAssignInspection = async (inspectionId, staffId) => {
    try {
      await api.put(`/inspections/${inspectionId}/assign`, null, { params: { staff_id: staffId } });
      showToast('Inspection assigned successfully', 'success');
      await fetchInspections();
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error assigning inspection'), 'error');
      throw error;
    }
  };

  // Modifier une inspection
  const handleEditInspection = async (inspectionId, data) => {
    try {
      await api.put(`/inspections/${inspectionId}`, {
        scheduled_date: data.scheduled_date,
        scheduled_time: data.scheduled_time,
        template_id: data.template_id,
        zone_id: data.zone_id
      });
      showToast('Inspection updated successfully', 'success');
      await fetchInspections();
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error updating inspection'), 'error');
      throw error;
    }
  };

  // Supprimer une inspection
  const handleDeleteInspection = async (inspection) => {
    if (!window.confirm(`Delete inspection ${inspection.inspection_number}?`)) return;
    try {
      await api.delete(`/inspections/${inspection.id}`, { data: { reason: 'Deleted by user' } });
      showToast(`Inspection ${inspection.inspection_number} deleted successfully`, 'success');
      await fetchInspections();
    } catch (error) {
      showToast(formatErrorMessage(error, 'Error deleting inspection'), 'error');
    }
  };

  const openCreateModal = () => {
    loadFormData();
    setShowCreateModal(true);
  };

  const openChecklist = (inspection) => {
    setSelectedInspection(inspection);
    setShowChecklistModal(true);
  };

  const closeChecklist = () => {
    setShowChecklistModal(false);
    setSelectedInspection(null);
    fetchInspections();
  };

  const openExportModal = (inspection) => {
    setSelectedInspection(inspection);
    setShowExportModal(true);
  };

  useEffect(() => {
    fetchCompanySettings();
    fetchInspections();
  }, [fetchInspections, fetchCompanySettings]);

  const filteredInspections = inspections.filter(insp => {
    const matchesSearch = 
      insp.inspection_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.zone_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || insp.category === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'text-green-400 bg-green-500/20';
      case 'scheduled': return 'text-blue-400 bg-blue-500/20';
      case 'in_progress': return 'text-yellow-400 bg-yellow-500/20';
      case 'overdue': return 'text-red-400 bg-red-500/20';
      case 'needs_review': return 'text-orange-400 bg-orange-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getResultColor = (result) => {
    switch(result) {
      case 'pass': return 'text-green-400';
      case 'fail': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const formatScheduledDateTime = (date, time) => {
    if (time) {
      return `${date} ${time.substring(0, 5)}`;
    }
    return date;
  };

  return (
    <div className="space-y-4 px-1">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt="Company Logo" 
              className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <ClipboardList size={18} className="text-blue-400" />
              </div>
              Inspections Management
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — FAA 150/5200-18C Compliant
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {['7d', '30d', '90d'].map(r => (
              <button key={r} onClick={() => setDateRange(r)}
                className={`px-3 py-1.5 transition-colors ${dateRange === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '3 Months'}
              </button>
            ))}
          </div>
          
          <button onClick={fetchInspections}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Scheduled" value={stats.scheduled} icon={Calendar} color="#38bdf8" />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="#10b981" />
        <StatCard title="Overdue" value={stats.overdue} icon={AlertCircle} color="#ef4444" />
        <StatCard title="Pass Rate" value={`${stats.passRate}%`} icon={Activity} color="#a78bfa" sub="Last 30 days" />
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by #, template, or zone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="overdue">Overdue</option>
        </select>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
        </select>
        
        <button 
          onClick={openCreateModal}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
        >
          <Plus size={14} /> New Inspection
        </button>
      </div>
      
      {/* Inspections Table */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Inspection #</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Template / Zone</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Assigned To</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Scheduled</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Result</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <Loader className="animate-spin mx-auto" />
                  </td>
                </tr>
              ) : filteredInspections.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    No inspections found
                  </td>
                </tr>
              ) : (
                filteredInspections.map((insp) => (
                  <React.Fragment key={insp.id}>
                    <tr className="border-b border-gray-700/50 hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-white font-mono text-sm">{insp.inspection_number}</td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm">{insp.template_name}</div>
                        <div className="text-gray-500 text-xs">{insp.zone_name || insp.zone_type || '—'}</div>
                      </td>
                      <td className="px-4 py-3">
                        {insp.assigned_to_name ? (
                          <span className="text-gray-300 text-sm flex items-center gap-1">
                            <User size={12} className="text-blue-400" />
                            {insp.assigned_to_name}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-xs">Unassigned</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-sm">
                        {formatScheduledDateTime(insp.scheduled_date, insp.scheduled_time)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(insp.status)}`}>
                          {insp.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {insp.overall_result && (
                          <span className={`text-sm font-medium ${getResultColor(insp.overall_result)}`}>
                            {insp.overall_result.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {insp.status !== 'completed' && (
                            <button
                              onClick={() => openChecklist(insp)}
                              className="p-1 hover:bg-gray-600 rounded text-green-400"
                              title="Perform Inspection"
                            >
                              <ClipboardList size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => { setSelectedInspection(insp); setShowAssignModal(true); loadFormData(); }}
                            className="p-1 hover:bg-gray-600 rounded text-blue-400"
                            title="Assign"
                          >
                            <User size={14} />
                          </button>
                          <button
                            onClick={() => { setSelectedInspection(insp); setShowEditModal(true); loadFormData(); }}
                            className="p-1 hover:bg-gray-600 rounded text-yellow-400"
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => openExportModal(insp)}
                            className="p-1 hover:bg-gray-600 rounded text-purple-400"
                            title="Export Report"
                          >
                            <Download size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteInspection(insp)}
                            className="p-1 hover:bg-gray-600 rounded text-red-400"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                          <button
                            onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}
                            className="p-1 hover:bg-gray-600 rounded text-gray-400"
                          >
                            {expandedId === insp.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === insp.id && (
                      <tr className="bg-gray-800/40">
                        <td colSpan="7" className="px-4 py-4">
                          <div className="space-y-2">
                            <p className="text-gray-300 text-sm">
                              <span className="text-gray-500">Description:</span> {insp.description || 'No description'}
                            </p>
                            <p className="text-gray-300 text-sm">
                              <span className="text-gray-500">Performed by:</span> {insp.performed_by_name || 'Not yet performed'}
                            </p>
                            <p className="text-gray-300 text-sm">
                              <span className="text-gray-500">Performed at:</span> {insp.performed_date || '—'}
                            </p>
                            {insp.findings_summary && (
                              <p className="text-gray-300 text-sm">
                                <span className="text-gray-500">Findings:</span> {insp.findings_summary}
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* Modals */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold">New Inspection</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Template *</label>
                <select
                  value={createForm.template_id}
                  onChange={(e) => setCreateForm({...createForm, template_id: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Select a template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_code} - {t.template_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Zone</label>
                <select
                  value={createForm.zone_id}
                  onChange={(e) => setCreateForm({...createForm, zone_id: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Select a zone...</option>
                  {zones.map(z => (
                    <option key={z.id} value={z.id}>{z.zone_code} - {z.zone_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Scheduled Date</label>
                <input
                  type="date"
                  value={createForm.scheduled_date}
                  onChange={(e) => setCreateForm({...createForm, scheduled_date: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Scheduled Time</label>
                <input
                  type="time"
                  value={createForm.scheduled_time}
                  onChange={(e) => setCreateForm({...createForm, scheduled_time: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                  step="60"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Assign To (optional)</label>
                <select
                  value={createForm.assigned_to}
                  onChange={(e) => setCreateForm({...createForm, assigned_to: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Unassigned</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.role})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
                Cancel
              </button>
              <button
                onClick={handleCreateInspection}
                disabled={creating || !createForm.template_id}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm"
              >
                {creating ? 'Creating...' : 'Create Inspection'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showAssignModal && selectedInspection && (
        <AssignModal
          inspection={selectedInspection}
          onAssign={handleAssignInspection}
          onClose={() => { setShowAssignModal(false); setSelectedInspection(null); }}
          staff={staff}
          loading={loading}
        />
      )}
      
      {showEditModal && selectedInspection && (
        <EditModal
          inspection={selectedInspection}
          onSave={handleEditInspection}
          onClose={() => { setShowEditModal(false); setSelectedInspection(null); }}
          templates={templates}
          zones={zones}
        />
      )}
      
      {showChecklistModal && selectedInspection && (
        <InspectionChecklist
          inspectionId={selectedInspection.id}
          onClose={closeChecklist}
          onComplete={closeChecklist}
        />
      )}
      
      {showExportModal && selectedInspection && (
        <InspectionExportModal
          inspectionId={selectedInspection.id}
          onClose={() => { setShowExportModal(false); setSelectedInspection(null); }}
        />
      )}
      
      {/* Toast Notification */}
      {toast && (
        <ToastNotification 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
    </div>
  );
};

export default InspectionsView;